// ✅ BlockSuspend.jsx — Live (onSnapshot), debounced search, tolerant dates, adminLogs auditing
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore';

/* // Optional scaffold (commented): tiny admin guard hook
const useAdminClaim = () => {
  // const [isAdmin, setIsAdmin] = useState(false);
  // useEffect(() => { /* read custom claim or Firestore flag *\/ setIsAdmin(true); }, []);
  // return isAdmin;
  return true;
};
*/

// ---------- Helpers ----------
const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const coerceDate = (d) => {
  try {
    if (!d) return null;
    if (d?.seconds) return new Date(d.seconds * 1000);
    if (typeof d === 'number') return new Date(d);
    if (typeof d === 'string') {
      const parsed = new Date(d);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (d instanceof Date) return d;
    return null;
  } catch {
    return null;
  }
};
const fmtDate = (d) => (d ? d.toLocaleDateString() : '—');

// ---------- Component ----------
export default function BlockSuspend() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);

  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const mountedRef = useRef(true);

  const showToast = useCallback((text, tone = 'neutral') => {
    setToast({ text, tone });
    setTimeout(() => setToast(null), 2000);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const col = collection(db, 'users');
    const unsub = onSnapshot(
      col,
      (snap) => {
        if (!mountedRef.current) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsers(list);
        setLoading(false);
      },
      (err) => {
        console.error('❌ Users snapshot error:', err);
        if (mountedRef.current) {
          setError('Failed to subscribe to users.');
          setLoading(false);
        }
      }
    );
    return () => {
      mountedRef.current = false;
      unsub();
    };
  }, []);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const base = q
      ? users.filter(
          (u) =>
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
        )
      : users;
    // Stable sort by name then email
    return [...base].sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      const ae = (a.email || '').toLowerCase();
      const be = (b.email || '').toLowerCase();
      if (ae < be) return -1;
      if (ae > be) return 1;
      return 0;
    });
  }, [users, debouncedSearch]);

  const toggleStatus = useCallback(
    async (userId, field) => {
      try {
        const user = users.find((u) => u.id === userId);
        if (!user) return;

        const newValue = !user[field];
        const confirmMsg = `Are you sure you want to ${newValue ? field : 'remove ' + field}?`;
        if (!window.confirm(confirmMsg)) return;

        setUpdatingId(userId);
        setError('');

        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          [field]: newValue,
          [`${field}Timestamp`]: newValue ? serverTimestamp() : null,
        });

        // ✅ Admin audit log (use adminLogs per data model)
        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        await addDoc(collection(db, 'adminLogs'), {
          timestamp: serverTimestamp(),
          adminId: currentUser?.uid || 'unknown',
          action: newValue ? `enable_${field}` : `disable_${field}`,
          targetUserId: userId,
          details: {
            email: user.email || '',
            field,
            newValue,
            prevValue: !!user[field],
          },
        });

        showToast(
          `${newValue ? 'Enabled' : 'Disabled'} ${field} for ${user.email || userId}`,
          'good'
        );
      } catch (e) {
        console.error('❌ Toggle status failed:', e);
        setError('Failed to update user status.');
        showToast('Update failed', 'bad');
      } finally {
        setUpdatingId(null);
      }
    },
    [users, showToast]
  );

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>
      <h2 style={styles.title}>🚫 Block / Suspend Users</h2>

      <input
        type="text"
        placeholder="Search by name or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={styles.search}
        aria-label="Search users"
      />

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}
      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading…</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Blocked</th>
              <th style={styles.th}>Suspended</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => {
              const blockedAt = fmtDate(coerceDate(user.blockedTimestamp));
              const suspendedAt = fmtDate(coerceDate(user.suspendedTimestamp));
              return (
                <tr key={user.id} style={styles.row}>
                  <td style={styles.td}>{user.name || '—'}</td>
                  <td style={styles.td}>{user.email || '—'}</td>
                  <td style={styles.td}>
                    {user.blocked ? `✅ (${blockedAt})` : '❌'}
                  </td>
                  <td style={styles.td}>
                    {user.suspended ? `✅ (${suspendedAt})` : '❌'}
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => toggleStatus(user.id, 'blocked')}
                      style={styles.button}
                      disabled={updatingId === user.id}
                      title={user.blocked ? 'Unblock user' : 'Block user'}
                    >
                      {updatingId === user.id && '… '}
                      {user.blocked ? 'Unblock' : 'Block'}
                    </button>
                    <button
                      onClick={() => toggleStatus(user.id, 'suspended')}
                      style={styles.button}
                      disabled={updatingId === user.id}
                      title={user.suspended ? 'Unsuspend user' : 'Suspend user'}
                    >
                      {updatingId === user.id && '… '}
                      {user.suspended ? 'Unsuspend' : 'Suspend'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {toast && (
        <div
          style={{
            ...styles.toast,
            ...(toast.tone === 'good'
              ? { background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }
              : toast.tone === 'bad'
              ? { background: '#fef2f2', color: '#991b1b', borderColor: '#fecaca' }
              : { background: '#f3f4f6', color: '#111827', borderColor: '#e5e7eb' }),
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

// ---------- Styles (kept layout) ----------
const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '1rem',
  },
  search: {
    marginBottom: '1.5rem',
    padding: '0.5rem',
    fontSize: '0.95rem',
    width: '100%',
    maxWidth: '320px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
  },
  error: {
    padding: '0.6rem 0.8rem',
    borderRadius: 6,
    border: '1px solid #fecaca',
    color: '#991b1b',
    background: '#fef2f2',
    marginBottom: '0.75rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    backgroundColor: '#e0f2f1',
    fontWeight: '600',
    color: '#000',
    fontSize: '0.875rem',
  },
  td: {
    padding: '12px',
    fontSize: '0.875rem',
    color: '#333',
  },
  row: {
    borderBottom: '1px solid #eee',
  },
  button: {
    marginRight: '0.5rem',
    padding: '0.4rem 0.8rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    backgroundColor: '#065f46',
    color: '#fff',
    fontSize: '0.8rem',
  },
  toast: {
    position: 'fixed',
    right: 16,
    bottom: 16,
    border: '1px solid',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    zIndex: 9999,
  },
};
